// 产品目录英文文案（按 slug / 分类 id 覆盖）。缺失字段自动回退中文。
export const categoriesEn = {
  llm: { name: 'Industrial Large Models', subtitle: 'Vertical-domain intelligence foundations built around large models' },
  energy: { name: 'Energy', subtitle: 'Thermal power, wind power, renewable-energy trading and intelligent operations' },
  machinery: { name: 'Machinery', subtitle: 'Intelligent operations, process optimization and quality prediction for equipment' },
  'digital-twin': { name: 'Digital Twin', subtitle: 'Virtual-real fusion diagnosis and prediction for key equipment and systems' },
  chemical: { name: 'Chemicals', subtitle: 'Intelligent control and energy saving for chemical production' },
  carbon: { name: 'Zero-Carbon Park', subtitle: 'Integrated dual-carbon management for buildings, enterprises and parks' },
  hardware: { name: 'Smart Hardware', subtitle: 'Robots, sensors and edge computing' }
}

const robotGalleryEn = [
  { name: 'High-Voltage Inspection Robot', tag: 'Inspection', desc: 'Distribution rooms, indoor switchgear rooms, telecom rooms' },
  { name: 'Lifting Wheeled Inspection Robot', tag: 'Inspection', desc: 'Equipment rooms, distribution rooms, GIS rooms, turbine halls' },
  { name: '6-DOF Wheeled Inspection Robot', tag: 'Inspection', desc: 'Indoor switchgear, GIS rooms, turbine halls, main plant' },
  { name: 'Outdoor Wheeled Inspection Robot', tag: 'Inspection', desc: 'Step-up stations, outdoor switchgear, plant areas' },
  { name: 'Wheeled Explosion-Proof Robot', tag: 'Inspection', desc: 'Parks, oil depots, process units' },
  { name: 'Rail-Mounted Inspection Robot', tag: 'Inspection', desc: 'Equipment rooms, distribution rooms' },
  { name: 'High-Protection 4WD Robot', tag: 'Special Ops', desc: 'GIS rooms, turbine halls, circulating pump rooms' },
  { name: 'Strong-Magnetic Inspection Robot', tag: 'Special Ops', desc: 'Electrolytic cell bottoms, rectifier rooms and other strong-field areas' },
  { name: 'Low-Voltage Switchroom Robot', tag: 'Special Ops', desc: 'Low-voltage distribution rooms and similar settings' },
  { name: 'Industrial Logistics Robot', tag: 'Logistics', desc: 'Semiconductor, electronics and new energy industries' }
]

const sensorGalleryEn = [
  { name: 'Vibration Energy Harvester', tag: 'Self-Powered', desc: 'Harvests vibration energy, wiring-free and battery-free' },
  { name: 'Breeze Energy Harvester', tag: 'Self-Powered', desc: 'Harvests light-wind energy for continuous self-supply' },
  { name: 'Magnetic-Field Harvester', tag: 'Self-Powered', desc: 'Harvests magnetic-field energy for stable supply' },
  { name: 'Deformation Sensor', tag: 'Sensing', desc: 'Monitors structural deformation and stress' },
  { name: 'Temp-Vibration Sensor', tag: 'Sensing', desc: 'Simultaneous temperature and vibration monitoring' },
  { name: 'Acoustic Sensor', tag: 'Sensing', desc: 'Captures equipment acoustic signatures' },
  { name: 'Temperature Sensor', tag: 'Sensing', desc: 'Precise equipment temperature monitoring' },
  { name: 'Self-Powered Sensor', tag: 'Self-Powered', desc: 'No external power, maintenance-free for the long term' }
]

const computeGalleryEn = [
  { name: 'Entry Configuration', tag: '2U', desc: '128/256 TFLOPs computing' },
  { name: 'Low Configuration', tag: '4U', desc: '128 TFLOPs computing' },
  { name: 'Mid Configuration', tag: '4U', desc: '256 TFLOPs computing' },
  { name: 'High Configuration', tag: '8U', desc: '512 TFLOPs computing' },
  { name: 'Ultra Configuration', tag: '46U', desc: '1024 TFLOPs computing' },
  { name: 'Edge Computing Box', tag: 'Edge', desc: 'Integrated Huawei 310P with built-in industry LLM' }
]

export const productsEn = {
  powermind: {
    name: 'PowerMind Power Industry Large Model',
    tagline: 'A smart brain that understands power, making expertise available to all',
    intro: "A vertical-domain large model for the power industry that distills senior experts' experience into an intelligent assistant available to everyone. Its capabilities span power knowledge Q&A, dynamic unit optimization, standardized operation verification, multi-dimensional fault attribution, emergency response plans and safety-code matching, making it widely applicable to thermal power, wind power, photovoltaics and power-grid scenarios.",
    values: [
      { title: 'Knowledge Q&A · Code Matching', desc: 'Query power knowledge and safety codes in one sentence, with millisecond-level accurate matching.' },
      { title: 'Unit Optimization · Operation Check', desc: 'Assists dynamic unit optimization and automatically verifies standardized operations.' },
      { title: 'Fault Attribution · Emergency Plans', desc: 'Locates faults via multi-dimensional attribution and generates emergency plans in one click.' }
    ]
  },
  industmind: {
    name: 'IndustMind Industrial Large Model',
    tagline: 'A "process brain" for manufacturers, making expertise reusable',
    intro: 'A "process brain" for manufacturing enterprises that helps improve quality, cut costs and boost efficiency. It covers process parameter optimization, intelligent process generation, machining quality prediction, intelligent part selection, knowledge Q&A and anomaly recognition, and is widely applicable to discrete industries such as machinery, so process knowledge no longer depends on a few veteran experts.',
    values: [
      { title: 'Process Optimization · Generation', desc: 'Automatically optimizes process parameters and generates process plans in one click.' },
      { title: 'Quality Prediction · Anomaly ID', desc: 'Predicts quality during machining and identifies anomalies in real time.' },
      { title: 'Smart Selection · Knowledge Q&A', desc: 'Assists intelligent part selection and answers process knowledge on demand.' }
    ]
  },
  mtmind: {
    name: 'OpsMind Intelligent O&M Large Model',
    tagline: 'One base model for all equipment O&M scenarios',
    intro: "China's first general-purpose intelligent operations base model, fusing multimodal data such as equipment images, vibration and current to provide unified condition monitoring, fault diagnosis and life prediction. No separate model is needed for each equipment type. A single model applies to power plants, chemicals, machinery, rail transit, industrial robots and other equipment operations scenarios.",
    values: [
      { title: 'One Base Model', desc: 'Unified multimodal modeling for all industries and equipment types.' },
      { title: 'Monitoring & Warning', desc: 'Condition monitoring, fault diagnosis and life prediction catch risks before failure.' },
      { title: 'Smarter Over Time', desc: 'Supports few-shot adaptation and continuous learning, becoming smarter as more scenarios are deployed.' }
    ]
  },
  powertrade: {
    name: 'Power Spot Trading System',
    tagline: 'Full-process intelligent managed services for power spot trading, focused on returns',
    intro: 'Provides full-process intelligent managed services for power spot trading for renewable-energy stations and power retailers, covering forecasting, bidding and performance review in one place.',
    values: [
      { title: 'Accurate Forecasting', desc: 'Forecasts price and volume from multi-source data to seize trading opportunities.' },
      { title: 'Worry-Free Managed Service', desc: 'End-to-end trading services that lower overall operating cost.' },
      { title: 'Return-Oriented', desc: 'Reviewable strategies and traceable processes for continuous return optimization.' }
    ]
  },
  'wind-om': {
    name: 'Wind Power Intelligent O&M System',
    tagline: 'Detect turbine risks in seconds, warn early, cut cost and boost output',
    intro: 'Built on the power-industry large-model foundation, it detects turbine blade cracks, gearbox pitting and other risks in seconds, shifting wind-farm operations from reactive repair to proactive prevention.',
    values: [
      { title: 'Early Warning', desc: 'Warns of faults 7-14 days ahead, reducing unplanned downtime.' },
      { title: 'Lower Cost', desc: 'Major-component replacement cost reduced by about 12%.' },
      { title: 'More Output', desc: 'Farm-wide generation up by about 2%, with notable annual gains.' }
    ]
  },
  'thermal-om': {
    name: 'Thermal Power Intelligent Operations System',
    tagline: 'One-day deployment, 20-minute response, payback in about a year',
    intro: 'A rapidly deployable intelligent operations solution for thermal power plants that goes live without halting production, upgrading periodic inspection to on-demand checks.',
    values: [
      { title: 'Fast Deployment', desc: 'Non-intrusive deployment in one day, no production halt or drilling.' },
      { title: 'Fast Response', desc: 'Anomalies pushed within 20 minutes, greatly shortening fault response time.' },
      { title: 'Fast Payback', desc: 'Higher availability and lower maintenance cost, payback in about 12 months.' }
    ]
  },
  biofuel: {
    name: 'Biomass Fuel Intelligent Analysis System',
    tagline: 'Quantified calorific value, traceable inspection, fairer settlement',
    intro: 'A fuel calorific-value analysis and quality-control platform for biomass power plants that addresses fuel adulteration and unclear calorific value.',
    values: [
      { title: 'Quantifiable Value', desc: 'Accurately derives fuel calorific value from lab and historical data.' },
      { title: 'Anti-Cheating', desc: 'Automatically identifies abnormal batches with traceable inspection results.' },
      { title: 'Fair Settlement', desc: 'More stable combustion and fairer settlement, reducing cost overall.' }
    ]
  },
  valve: {
    name: 'High-End Control Valve Selection & Design System',
    tagline: 'From ten to over a thousand selections per day, fast and accurate',
    intro: 'Reinvents high-end control-valve selection and design with large models, turning expert-dependent, tedious selection into a fast and accurate intelligent service.',
    values: [
      { title: 'Efficiency Leap', desc: 'Selection capacity rises from about 10 to over 1,000 per day.' },
      { title: 'Quality Assured', desc: 'Selection accuracy near 100%, greatly reducing rework.' },
      { title: 'Lower Barrier', desc: 'Greatly reduces reliance on senior selection engineers.' }
    ]
  },
  'machine-quality': {
    name: 'Machining Quality Prediction System',
    tagline: 'Predict quality during machining, eliminate defects before they occur',
    intro: 'Predicts part quality in real time during machining so quality issues are caught before they appear, reducing scrap and rework.',
    values: [
      { title: 'Real-Time Prediction', desc: 'Predicts surface quality during machining, no waiting for inspection.' },
      { title: 'Less Scrap', desc: 'Catches anomalies early, lowering scrap rate and rework cost.' },
      { title: 'Easy to Deploy', desc: 'Plug-and-play sensors connect quickly to existing lines.' }
    ]
  },
  'process-optimize': {
    name: 'Part Machining Process Parameter Optimization System',
    tagline: 'Intelligently optimize process parameters, end trial-and-error',
    intro: 'Automatically optimizes machining process parameters based on industrial large models, helping enterprises steadily improve efficiency and consistency.',
    values: [
      { title: 'Auto Optimization', desc: 'Recommends optimal process parameters, ending repeated trial-and-error.' },
      { title: 'Quality & Efficiency', desc: 'Improves machining consistency and efficiency.' },
      { title: 'Knowledge Retention', desc: 'Turns process know-how into reusable enterprise assets.' }
    ]
  },
  anomaly: {
    name: 'Part Anomaly Detection System',
    tagline: 'Real-time monitoring of the whole machining process, guarding quality',
    intro: 'Efficiently monitors and intelligently analyzes the entire part-machining process, detecting anomalies instantly to avoid batch losses.',
    values: [
      { title: 'Full-Process Monitoring', desc: 'Covers the whole machining process, leaving anomalies nowhere to hide.' },
      { title: 'Timely Warning', desc: 'Identifies anomalies in real time and alarms, reducing batch loss.' },
      { title: 'Smart Analysis', desc: 'Automatically locates problems and assists rapid handling.' }
    ]
  },
  schedule: {
    name: 'Multi-Order Production Progress Prediction System',
    tagline: 'See multi-order progress clearly, plan delivery with confidence',
    intro: 'For multi-order discrete manufacturing workshops, it accurately predicts the progress of each order, making scheduling and delivery planning more scientific.',
    values: [
      { title: 'Transparent Progress', desc: 'Predicts each order\u2019s production progress in real time.' },
      { title: 'On-Time Delivery', desc: 'Identifies delay risks early to ensure on-time delivery.' },
      { title: 'Scientific Scheduling', desc: 'Provides data support for scheduling and dispatching.' }
    ]
  },
  metro: {
    name: 'Metro Intelligent Operations System',
    tagline: 'Real-time health assessment for safe, reliable train operation',
    intro: 'A smart operations monitoring platform for metro systems that safeguards trains and station equipment through real-time health assessment and intelligent diagnosis.',
    values: [
      { title: 'Real-Time Health', desc: 'Multi-level health assessment makes equipment status clear at a glance.' },
      { title: 'Smart Diagnosis', desc: 'Intelligent fault diagnosis and prediction to prevent problems.' },
      { title: 'Safe & Reliable', desc: 'Raises the digital and intelligent level of metro operations.' }
    ]
  },
  'robot-diagnosis': {
    name: 'Industrial Robot Intelligent Diagnosis System',
    tagline: 'Safeguard industrial robots and optimize maintenance strategy',
    intro: 'Developed under a national key program, it provides intelligent fault diagnosis and health assessment for industrial robots, raising the utilization of automated lines.',
    values: [
      { title: 'Broad Compatibility', desc: 'Already applied on KUKA, GSK, Inovance and other robots.' },
      { title: 'Health Assessment', desc: 'Assesses robot health status in real time.' },
      { title: 'Optimized Maintenance', desc: 'Improves maintenance strategy and line utilization.' }
    ]
  },
  'gear-life-twin': {
    name: 'Heavy-Duty Transmission Gear Life Prediction System',
    tagline: 'See the life of key components, plan maintenance calmly',
    intro: 'A digital-twin system for heavy-duty transmission equipment that continuously assesses gear health and life trends, helping enterprises schedule maintenance ahead and reduce sudden downtime.',
    values: [
      { title: 'Visible Life', desc: 'Clearly presents the life trend of key gears.' },
      { title: 'Planned Maintenance', desc: 'Schedules maintenance windows ahead, reducing reactive repairs.' },
      { title: 'Lower Risk', desc: 'Reduces the risk of major equipment failure and downtime.' }
    ]
  },
  'thermal-core-twin': {
    name: 'Thermal Power Core Component Diagnosis System',
    tagline: 'Grasp core-component status in real time, find risks early',
    intro: 'A digital-twin diagnosis system for key thermal-power equipment that helps operations teams understand core-component health in time and improve operational reliability.',
    values: [
      { title: 'Condition Sensing', desc: 'Core-component health status is clear at a glance.' },
      { title: 'Early Warning', desc: 'Detects abnormal trends early and assists rapid handling.' },
      { title: 'Stable Operation', desc: 'Improves unit operating safety and continuity.' }
    ]
  },
  'power-equipment-twin': {
    name: 'Power Plant Key Equipment O&M Large Model',
    tagline: 'Connect equipment, models and operations decisions via digital twins',
    intro: 'A digital-twin operations platform for key power-plant equipment that consolidates equipment status, health assessment and operations recommendations for more intuitive management and efficient decisions.',
    values: [
      { title: 'Equipment Twin', desc: 'Centralized modeling and display of key equipment status.' },
      { title: 'Unified Management', desc: 'Unified presentation across equipment and systems.' },
      { title: 'Decision Support', desc: 'Provides a basis for maintenance, inspection and operations decisions.' }
    ]
  },
  ammonia: {
    name: 'Denitration Precise Ammonia Injection System',
    tagline: 'Precise ammonia control and lower ammonia slip, delivering economic and environmental value',
    intro: 'An intelligent precise ammonia-injection control system for power and chemical boilers that precisely controls ammonia and reduces ammonia slip for clear economic and environmental benefits.',
    values: [
      { title: 'Cost Saving', desc: 'Saves about 10% urea injection, with hundreds of thousands in annual benefit per unit.' },
      { title: 'Fewer Penalties', desc: 'Reduces environmental penalties from excessive ammonia slip.' },
      { title: 'Equipment Protection', desc: 'Reduces ash buildup and corrosion, extending equipment life.' }
    ]
  },
  'chem-park': {
    name: 'Chemical Park Intelligent Control System',
    tagline: 'Intelligent control of the whole process, balancing safety and efficiency',
    intro: 'An intelligent control solution for chemical parks that integrates industrial large models for whole-process monitoring, process optimization and safety risk control.',
    values: [
      { title: 'Intelligent Control', desc: 'Efficient monitoring and smart analysis across the whole process.' },
      { title: 'Process Optimization', desc: 'Continuously optimizes process and quality with large models.' },
      { title: 'Safety Risk Control', desc: 'Intelligent risk warning for better safety and compliance.' }
    ]
  },
  'carbon-platform': {
    name: 'Digital Carbon Management Platform',
    tagline: 'An integrated platform from carbon monitoring and accounting to optimization',
    intro: 'An integrated digital carbon-management platform offering full-process automation from carbon-emission monitoring and accounting to energy-saving optimization.',
    values: [
      { title: 'Panoramic Monitoring', desc: 'Real-time, visual monitoring of carbon emissions and energy use.' },
      { title: 'Smart Accounting', desc: 'Automated carbon accounting and reporting, saving time and effort.' },
      { title: 'Energy & Carbon Saving', desc: 'Identifies saving potential to support dual-carbon goals.' }
    ]
  },
  'carbon-gov': {
    name: 'Government and Industrial Park Carbon Management',
    tagline: 'Panoramic energy-carbon monitoring with green-development decision support',
    intro: 'A carbon-management platform for governments and industrial parks, enabling panoramic energy-carbon monitoring and green, low-carbon development assessment.',
    values: [
      { title: 'Regional Monitoring', desc: 'Panoramic energy-carbon monitoring of the park.' },
      { title: 'Enterprise Profiling', desc: 'Carbon profiling of key enterprises for precise management.' },
      { title: 'Decision Support', desc: 'Supports green, low-carbon development decisions.' }
    ]
  },
  'carbon-enterprise': {
    name: 'Enterprise Carbon Management',
    tagline: 'Know your carbon baseline, optimize energy, stay green-compliant',
    intro: 'A carbon-management solution for enterprises that helps clarify the carbon baseline, optimize energy structure and achieve green, low-carbon operations.',
    values: [
      { title: 'Fine Accounting', desc: 'Fine-grained carbon accounting for a crystal-clear baseline.' },
      { title: 'Energy Optimization', desc: 'Optimizes energy structure and lowers energy cost.' },
      { title: 'Green Compliance', desc: 'Meets green compliance requirements and lifts brand value.' }
    ]
  },
  'carbon-building': {
    name: 'Building Carbon Management',
    tagline: 'Fine-grained management and optimization of building energy use',
    intro: 'A carbon-management solution for buildings that finely manages and optimizes building energy consumption and carbon emissions.',
    values: [
      { title: 'Energy Monitoring', desc: 'Real-time monitoring of building energy use with visible issues.' },
      { title: 'Energy Optimization', desc: 'Intelligently optimizes energy use and lowers operating cost.' },
      { title: 'Green Buildings', desc: 'Enables green buildings and low-carbon operation.' }
    ]
  },
  robots: {
    name: 'Robots',
    tagline: 'Inspection · special operations · logistics, replacing frequent and hazardous tasks',
    intro: 'Industrial robots covering inspection, special operations and logistics, adapted to grid, coal and petrochemical industries, replacing manual frequent, hazardous and repetitive work.',
    values: [
      { title: 'Multi-Scenario', desc: 'Adapted to grid, coal, petrochemical and other industry scenarios.' },
      { title: 'Replace Hazard', desc: 'Replaces humans in hazardous, strong-field and explosion-proof environments.' },
      { title: '24/7 Operation', desc: 'All-weather autonomous operation, stable and efficient.' }
    ],
    gallery: robotGalleryEn
  },
  sensors: {
    name: 'Sensors',
    tagline: 'Self-powered and wiring-free, a stable and reliable data source',
    intro: 'Self-developed industrial sensors and energy harvesters, some self-powered and free of wiring or battery replacement, providing a stable, reliable data source for condition monitoring.',
    values: [
      { title: 'Self-Powered', desc: 'Energy harvesters are self-powered, wiring-free and battery-free.' },
      { title: 'Multi-Physical', desc: 'Covers vibration, temperature, sound, deformation and more.' },
      { title: 'Stable & Reliable', desc: 'Industrial-grade quality, maintenance-free for the long term.' }
    ],
    gallery: sensorGalleryEn
  },
  'edge-compute': {
    name: 'Edge Computing',
    tagline: 'Multi-tier computing + edge box, safer on-premises deployment',
    intro: 'Multi-tier computing solutions from entry to ultra configuration, plus an edge computing box integrating the Huawei 310P chip, enabling on-premises deployment of vertical-domain large models.',
    values: [
      { title: 'Multiple Tiers', desc: 'From entry to ultra configuration to flexibly match needs.' },
      { title: 'On-Premise', desc: 'Edge box integrates Huawei 310P with a built-in industry LLM.' },
      { title: 'Data Security', desc: 'Data stays on-site, safeguarding enterprise data security.' }
    ],
    gallery: computeGalleryEn
  }
}
